import React, { useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { db } from "../../config/firebase";
import { collection, getDocs, writeBatch, doc } from "firebase/firestore";
import "./Main.css";

const RESET_OPTIONS = [
  {
    id: "signatures",
    label: "איפוס חתימות",
    description: "ימחק את כל החתימות ודפי עמוד החייל שנוצרו.",
    detail: [
      "כל החתימות (assignments)",
      "כל עמודי החייל שנוצרו (employeePages)",
    ],
    collections: ["assignments", "employeePages"],
    color: "#e53e3e",
  },
  {
    id: "history",
    label: "איפוס היסטוריה",
    description: "ימחק את כל ההיסטוריה של הפעולות.",
    detail: [
      "כל ההיסטוריה (history)",
    ],
    collections: ["history"],
    color: "#805ad5",
  },
  {
    id: "items",
    label: "איפוס מלאי",
    description: "ימחק את כל פריטי המלאי. הגדרות הפריטים לא יימחקו.",
    detail: [
      "כל פריטי המלאי (inventory)",
    ],
    collections: ["inventory"],
    color: "#dd6b20",
  },
  {
    id: "all",
    label: "איפוס הכל",
    description: "ימחק את כל הנתונים — חתימות, היסטוריה, עמודי חייל ומלאי. הגדרות הפריטים לא יימחקו.",
    detail: [
      "כל החתימות (assignments)",
      "כל עמודי החייל שנוצרו (employeePages)",
      "כל ההיסטוריה (history)",
      "כל פריטי המלאי (inventory)",
    ],
    collections: ["assignments", "employeePages", "history", "inventory"],
    color: "#742a2a",
  },
];

async function deleteCollection(colName) {
  const snap = await getDocs(collection(db, colName));
  if (snap.empty) return 0;
  // Batch delete in chunks of 400
  const chunks = [];
  let current = [];
  snap.docs.forEach((d) => {
    current.push(d.ref);
    if (current.length === 400) { chunks.push(current); current = []; }
  });
  if (current.length) chunks.push(current);
  for (const chunk of chunks) {
    const batch = writeBatch(db);
    chunk.forEach((ref) => batch.delete(ref));
    await batch.commit();
  }
  return snap.size;
}

const Main = () => {
  const { user } = useAuth();
  const isAdmin = user?.permissions === "admin";

  const [selected, setSelected] = useState(null); // reset option id
  const [confirmText, setConfirmText] = useState("");
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(null); // { label, count }

  const selectedOption = RESET_OPTIONS.find((o) => o.id === selected);
  const CONFIRM_WORD = "מחק";
  const confirmed = confirmText.trim() === CONFIRM_WORD;

  const handleReset = async () => {
    if (!selectedOption || !confirmed) return;
    setRunning(true);
    let total = 0;
    try {
      for (const col of selectedOption.collections) {
        total += await deleteCollection(col);
      }
      setDone({ label: selectedOption.label, count: total });
    } catch (e) {
      alert("שגיאה בעת המחיקה: " + e.message);
    } finally {
      setRunning(false);
      setSelected(null);
      setConfirmText("");
    }
  };

  return (
    <div className="main-container">
      <header className="main-header">
        <h1>עמוד ראשי</h1>
      </header>

      <main className="main-content">
        <div className="welcome-section">
          <h2>ברוכים הבאים ללוח הבקרה</h2>
          <p>זהו העמוד הראשי שלכם. תוכלו להתחיל לבנות את האפליקציה שלכם כאן.</p>
          <div className="user-info">
            <h3>מידע משתמש</h3>
            <p>
              <strong>אימייל:</strong>{" "}
              {user?.email || "לא זמין"}
            </p>
            <p>
              <strong>הרשאות:</strong>{" "}
              {user?.permissions === "admin"
                ? "מנהל"
                : user?.permissions === "parentsUser"
                ? "משתמש הורים"
                : user?.permissions === "childUser"
                ? "משתמש ילד"
                : "לא זמין"}
            </p>
            <p>
              <strong>מספר עובד:</strong>{" "}
              {user?.employeeId || "לא זמין"}
            </p>
          </div>
        </div>

        {/* ── Admin Reset Section ─────────────────────────── */}
        {isAdmin && (
          <div className="reset-section">
            <div className="reset-section-header">
              <span className="reset-danger-icon">⚠️</span>
              <h3>איפוס מסד נתונים</h3>
              <span className="reset-admin-badge">מנהל בלבד</span>
            </div>
            <p className="reset-section-desc">
              פעולות אלו בלתי הפיכות. בחר מה ברצונך למחוק:
            </p>

            {done && (
              <div className="reset-success-banner">
                ✅ {done.label} הושלם — נמחקו {done.count} רשומות.
                <button className="reset-success-close" onClick={() => setDone(null)}>×</button>
              </div>
            )}

            <div className="reset-options-row">
              {RESET_OPTIONS.map((opt) => (
                <div key={opt.id} className="reset-option-card">
                  <button
                    className="reset-option-btn"
                    style={{ "--reset-color": opt.color }}
                    onClick={() => { setSelected(opt.id); setConfirmText(""); setDone(null); }}
                  >
                    {opt.label}
                  </button>
                  <p className="reset-option-caption">{opt.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* ── Confirmation Modal ──────────────────────────────── */}
      {selected && selectedOption && (
        <div className="reset-modal-overlay" onClick={() => { setSelected(null); setConfirmText(""); }}>
          <div className="reset-modal" onClick={(e) => e.stopPropagation()}>
            <div className="reset-modal-header" style={{ background: selectedOption.color }}>
              <span>⚠️</span>
              <h2>{selectedOption.label}</h2>
            </div>

            <div className="reset-modal-body">
              <p className="reset-modal-desc">{selectedOption.description}</p>

              <div className="reset-modal-what">
                <p className="reset-modal-what-title">מה יימחק:</p>
                <ul>
                  {selectedOption.detail.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>

              <div className="reset-modal-warning">
                פעולה זו <strong>בלתי הפיכה לחלוטין</strong>. לא ניתן לשחזר את הנתונים לאחר המחיקה.
              </div>

              <div className="reset-modal-confirm-area">
                <label>
                  כדי לאשר, הקלד <strong>{CONFIRM_WORD}</strong> בשדה הבא:
                </label>
                <input
                  type="text"
                  className="reset-confirm-input"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={`הקלד "${CONFIRM_WORD}" לאישור`}
                  autoComplete="off"
                  autoFocus
                />
              </div>
            </div>

            <div className="reset-modal-actions">
              <button
                className="reset-modal-cancel"
                onClick={() => { setSelected(null); setConfirmText(""); }}
                disabled={running}
              >
                ביטול
              </button>
              <button
                className="reset-modal-confirm"
                style={{ background: confirmed && !running ? selectedOption.color : undefined }}
                onClick={handleReset}
                disabled={!confirmed || running}
              >
                {running ? "מוחק..." : "מחק לצמיתות"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Main;
